import { GoalThreshold } from '../../coaching-base/config';
import {
    CommitmentSummaryListItem,
    ObservationSummaryDto,
} from '../../coaching-base/general';
import {
    composeTagIdentifierNext,
    FocusIdentifier,
} from '../../coaching-tags/tags/tags.model';

import { CommitmentCategory } from './commitment-category.model';
import { CommitmentStatusesView } from './commitment-status-view.model';
import {
    commitmentSummaryViewToStatusViewTransformer,
    commitmentToStatusViewTransformer,
} from './commitment-status-view.transformer';
import { CommitmentSummaryStatusView } from './commitment-summary-status-view';
import { CommitmentSummaryView } from './commitment-summary-view.model';
import {
    commitmentSummaryToViewTransformer,
    determineActions,
} from './commitment-summary.transformer';
import { LocationView } from './location-view.model';
import { ObservationCategory } from './observation-category.model';
import { ObservationStatusesView } from './observation-status.model';
import { ObservationSummaryView } from './observation-summary.model';
import { observationSummaryToViewTransformer } from './observation-summary.transformer';
import { TranslationKeyMap } from './pillar-two.i18n';
import {
    StatusViewTypeColorMap,
    StatusViewTypeLabelMap,
} from './status-view-type.model';
import { StatusView } from './status-view.model';
import { TabRefId } from './tab-ref-id.model';

/** @internal only exported for testing */
export function checkMetricIdCommitmentInCategories(
    categories: CommitmentCategory[],
    activeMetricId: number,
): void {
    categories.forEach((cat, index) => {
        // commitmentGoalLookup could be leveraged but a dependence on that isn't wanted
        cat.containsMetricId = cat.items.some((e) =>
            e.goals.some((ee) => ee.metricId === activeMetricId),
        );

        // TODO revise. is it ok to do it here
        cat.items.forEach((e, ei) => {
            cat.items[ei] = {
                ...e,
                containsMetricId: e.goals.some(
                    (ee) => ee.metricId === activeMetricId,
                ),
            };
        });

        if (index !== 1 && index !== 2) {
            cat.isOpen = cat.containsMetricId || false;
        }
    });
}

function applyCommitmentOpenStates(categories: CommitmentCategory[]): void {
    const draft = categories[0];
    const active = categories[1];
    const otherActive = categories[2];
    const close = categories[3];
    const disputed = categories[4];

    active.isOpen = active.items.length !== 0;
    otherActive.isOpen = otherActive.items.length !== 0;
    draft.isOpen =
        active.items.length + otherActive.items.length === 0 &&
        draft.items.length !== 0;
    disputed.isOpen =
        active.items.length + otherActive.items.length === 0 &&
        disputed.items.length !== 0;
    close.isOpen = false;
}

/// /////// separator //////////

const otherStatusViewMap: {
    [
        key: number /* CommitmentSummaryStatusView */
    ]: CommitmentSummaryStatusView;
} = {
    [CommitmentSummaryStatusView.OTHER_PRE_APPROVAL]:
        CommitmentSummaryStatusView.PRE_APPROVAL,
    [CommitmentSummaryStatusView.OTHER_ACTIVE]:
        CommitmentSummaryStatusView.ACTIVE,
    [CommitmentSummaryStatusView.OTHER_UPDATING]:
        CommitmentSummaryStatusView.UPDATING,
    [CommitmentSummaryStatusView.OTHER_PENDING_CLOSE]:
        CommitmentSummaryStatusView.PENDING_CLOSE,
};

const otherStatusViewReverseMap: {
    [
        key: number /* CommitmentSummaryStatusView */
    ]: CommitmentSummaryStatusView;
} = {};
// eslint-disable-next-line no-return-assign
Object.keys(otherStatusViewMap)
    .map((e) => parseInt(e))
    .forEach((e) => (otherStatusViewReverseMap[otherStatusViewMap[e]] = e));

function resetStatusViewInCommitmentSummaryView(
    items: CommitmentSummaryView[],
    activeMetricId: number,
): CommitmentSummaryView[] {
    // reset all the OTHER_*
    return items
        .map((item) => {
            // eslint-disable-next-line no-prototype-builtins
            if (otherStatusViewReverseMap.hasOwnProperty(item.statusId)) {
                return {
                    ...item,
                    statusId: otherStatusViewReverseMap[item.statusId],
                };
            }
            return item;
        })
        .map((item) => {
            if (
                // eslint-disable-next-line no-prototype-builtins
                otherStatusViewMap.hasOwnProperty(item.statusId) &&
                item.goals &&
                item.goals.some((e) => e.metricId === activeMetricId)
            ) {
                return { ...item, statusId: otherStatusViewMap[item.statusId] };
            }
            return item;
        });
}

function filterActive(e: CommitmentSummaryView): boolean {
    return (
        (e.statusId === CommitmentSummaryStatusView.ACTIVE &&
            e.isOriginalCommitment &&
            !(e.approvalId || e.targetedApprovalId)) ||
        ((e.disputedByMe || e.disputedByOtherParticipant) &&
            e.statusId === CommitmentSummaryStatusView.ACTIVE) ||
        ((e.statusId === CommitmentSummaryStatusView.PRE_APPROVAL ||
            e.statusId === CommitmentSummaryStatusView.UPDATING ||
            e.statusId === CommitmentSummaryStatusView.PENDING_CLOSE) &&
            !e.isOriginalCommitment) // original commitments should never have pending statuses anyway
    );
}

function filterOtherActive(e: CommitmentSummaryView): boolean {
    return (
        (e.statusId === CommitmentSummaryStatusView.OTHER_ACTIVE &&
            e.isOriginalCommitment &&
            !(e.approvalId || e.targetedApprovalId)) ||
        ((e.disputedByMe || e.disputedByOtherParticipant) &&
            e.statusId === CommitmentSummaryStatusView.OTHER_ACTIVE) ||
        ((e.statusId === CommitmentSummaryStatusView.OTHER_PRE_APPROVAL ||
            e.statusId === CommitmentSummaryStatusView.OTHER_UPDATING ||
            e.statusId === CommitmentSummaryStatusView.OTHER_PENDING_CLOSE) &&
            !e.isOriginalCommitment) // original commitments should never have pending statuses anyway
    );
}

/// /////// separator //////////

export function reCategorizeCommitments(
    categories: CommitmentCategory[],
    activeMetricId: number,
): CommitmentCategory[] {
    if (
        !categories ||
        categories.length === 0 ||
        categories.some((e) => !e.items)
    ) {
        return [];
    }

    const next = [...categories];

    // these indexes are expected to be guaranteed. 1=active, 2=otherActive
    const items = [...next[1].items, ...next[2].items];

    const result = resetStatusViewInCommitmentSummaryView(
        items,
        activeMetricId,
    );

    const active = result.filter(filterActive);
    const otherActive = result.filter(filterOtherActive);

    next[1] = {
        ...next[1],
        items: active,
    };
    next[2] = {
        ...next[2],
        items: otherActive,
    };

    for (let i = 0; i < categories.length; i++) {
        if (i !== 1 && i !== 2) {
            next[i] = {
                ...next[i],
            };
        }
    }

    applyCommitmentOpenStates(next);
    checkMetricIdCommitmentInCategories(next, activeMetricId);

    return next;
}

export function categorizeCommitments(
    commitments: CommitmentSummaryListItem[],
    userId: number,
    activeMetricId: number,
    goalThresholds: GoalThreshold[],
): CommitmentCategory[] {
    const retTransformed = commitmentSummaryToViewTransformer(
        commitments,
        userId,
        activeMetricId,
        goalThresholds,
    );
    const ret = resetStatusViewInCommitmentSummaryView(
        retTransformed,
        activeMetricId,
    );

    const active = ret.filter(filterActive);

    const otherActive = ret.filter(filterOtherActive);

    const closed = ret.filter(
        (e) => e.statusId === CommitmentSummaryStatusView.CLOSED,
    );

    const draft = ret.filter(
        (e) =>
            (e.statusId === CommitmentSummaryStatusView.DRAFT &&
                !e.isOriginalCommitment &&
                !e.disputedByMe &&
                !e.disputedByOtherParticipant) ||
            ((e.statusId === CommitmentSummaryStatusView.ACTIVE ||
                e.statusId === CommitmentSummaryStatusView.OTHER_ACTIVE) &&
                !e.isOriginalCommitment),
    );

    const nonDisputeStatuses = [
        CommitmentSummaryStatusView.PRE_APPROVAL,
        CommitmentSummaryStatusView.OTHER_PRE_APPROVAL,
        CommitmentSummaryStatusView.UPDATING,
        CommitmentSummaryStatusView.OTHER_UPDATING,
        CommitmentSummaryStatusView.PENDING_CLOSE,
        CommitmentSummaryStatusView.OTHER_PENDING_CLOSE,
    ];
    const disputed = ret.filter((e) => {
        // maybe the existence of DraftAndApprovalResult.approvalId!=null/DraftAndApprovalResult.approved==null should be checked instead
        const isPendingApprovalAfterLastDispute = nonDisputeStatuses.includes(
            e.statusId,
        );

        return (
            !e.isOriginalCommitment &&
            (e.disputedByMe || e.disputedByOtherParticipant) &&
            !isPendingApprovalAfterLastDispute
        );
    });

    const categories: CommitmentCategory[] = [
        {
            label: TranslationKeyMap._9476_DRAFT,
            items: draft,
        },
        {
            label: TranslationKeyMap._9561_ACTIVE_FOR_THIS_METRIC,
            items: active,
        },
        {
            label: TranslationKeyMap._9806_ACTIVE_FOR_OTHER_METRIC,
            items: otherActive,
        },
        {
            label: TranslationKeyMap._9563_RECENTLY_CLOSED,
            items: closed,
        },
        {
            label: TranslationKeyMap._8703_DISPUTED,
            items: disputed,
        },
    ];

    applyCommitmentOpenStates(categories);
    checkMetricIdCommitmentInCategories(categories, activeMetricId);

    return categories;
}

function _categorizeObservations(
    items: ObservationSummaryView[],
    activeMetricId: number,
): ObservationCategory[] {
    const makeEmptyCategory = (
        identifier: string,
        behaviour: string,
        subBehaviour: string,
        published: boolean,
    ): ObservationCategory => {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const cat = {
            behaviour,
            subBehaviour,
            published,
            obsIdentifier: identifier,
            focusItems: {
                draftItems: {
                    label: TranslationKeyMap._9476_DRAFT,
                    isOpen: false,
                    focusContainers: [],
                },
                publishedItems: {
                    published, // TODO revise
                    isOpen: false,
                    label: TranslationKeyMap._9113_PUBLISHED,
                    focusContainers: [],
                },
            },
        } as ObservationCategory;
        cat.focusItemsArr = [
            cat.focusItems.publishedItems,
            cat.focusItems.draftItems,
        ];
        return cat;
    };

    // TODO all identifiers should be aligned
    // this is mainly for compatibility with migrated observations from the old coaching model
    const draftPublishedCategory = makeEmptyCategory(
        'null-null-true',
        null,
        null,
        true,
    );
    draftPublishedCategory.label = TranslationKeyMap._9821_PUBLISHED_UNFOCUSED;
    draftPublishedCategory.focusItemsArr.splice(1, 1);

    // TODO all identifiers should be aligned
    const draftUnpublishedCategory = makeEmptyCategory(
        'null-null-false',
        null,
        null,
        false,
    );
    draftUnpublishedCategory.label = TranslationKeyMap._9820_DRAFT_UNFOCUSED;
    draftUnpublishedCategory.focusItemsArr.splice(0, 1);

    const result: { [key: string]: ObservationCategory } = {};

    // TODO pivot inside observationSummaryToViewTransformer() instead of here
    items.forEach((item) => {
        let focusItem = result[item.obsIdentifier];

        // TODO all identifiers should be aligned
        if (!item.behaviour) {
            focusItem = item.published
                ? draftPublishedCategory
                : draftUnpublishedCategory;
        }

        if (!focusItem) {
            focusItem = makeEmptyCategory(
                item.obsIdentifier,
                item.behaviour,
                item.subBehaviour,
                item.published,
            );

            // draft categories seem to be exempt
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            focusItem.isOpen = item.focusContainers.some(
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                (ee) => ee.metrics && ee.metrics.includes(activeMetricId),
            );

            focusItem.focusItemsArr = [
                focusItem.focusItems.publishedItems,
                focusItem.focusItems.draftItems,
            ];
            result[item.obsIdentifier] = focusItem;
        }

        focusItem.focusItems.publishedItems.focusContainers.push(
            ...item.focusContainers.filter((e) => e.published),
        );
        focusItem.focusItems.draftItems.focusContainers.push(
            ...item.focusContainers.filter((e) => !e.published),
        );
    });

    const nextCategories = Object.values(result);
    const noOthers = nextCategories.length === 0;

    // for this category focusItemsArr.length should === 1
    if (draftPublishedCategory.focusItemsArr[0].focusContainers.length !== 0) {
        nextCategories.push(draftPublishedCategory);
    }

    // for this category focusItemsArr.length should === 1
    draftUnpublishedCategory.isOpen =
        noOthers &&
        draftUnpublishedCategory.focusItemsArr[0].focusContainers.length !== 0;
    nextCategories.push(draftUnpublishedCategory);

    return nextCategories;
}

export function reCategorizeObservations(
    categories: ObservationCategory[],
    activeMetricId: number,
): ObservationCategory[] {
    if (!categories || categories.length === 0) {
        return [];
    }

    const items = categories.map<ObservationSummaryView>((c) => ({
        behaviour: c.behaviour,
        subBehaviour: c.subBehaviour,
        published: c.published,
        isOpen: c.isOpen,
        obsIdentifier: c.obsIdentifier,
        focusContainers: c.focusItemsArr.reduce(
            (p, cc) => p.concat(cc.focusContainers || []),
            [],
        ),
    }));

    return _categorizeObservations(items, activeMetricId);
}

export function categorizeObservations(
    observations: ObservationSummaryDto[],
    activeMetricId: number,
): ObservationCategory[] {
    const ret = observationSummaryToViewTransformer(observations);
    return _categorizeObservations(ret, activeMetricId);
}

export function createCommitmentStatuses(
    commitmentCategories: CommitmentCategory[],
): CommitmentStatusesView {
    const result: CommitmentStatusesView = {};

    const items = (commitmentCategories || []).reduce<CommitmentSummaryView[]>(
        (p, c) => p.concat(c.items),
        [],
    );
    items.forEach((item) => {
        result[item.cmtIdentifier] =
            commitmentSummaryViewToStatusViewTransformer(item);
    });

    return result;
}

export function createInitialCommitmentStatuses(
    commitmentCategories: CommitmentCategory[],
): CommitmentStatusesView {
    const result: CommitmentStatusesView = {};

    const items = (commitmentCategories || []).reduce<CommitmentSummaryView[]>(
        (p, c) => p.concat(c.items),
        [],
    );
    items.forEach((item) => {
        result[item.cmtIdentifier] = commitmentToStatusViewTransformer(item);
    });

    return result;
}

export function createObservationStatuses(
    observations: ObservationSummaryDto[],
): ObservationStatusesView {
    const result = {};

    const items = observations || ([] as ObservationSummaryDto[]);
    items.forEach((item) => {
        result[item.event.eventId] = false;
    });

    return result;
}

export function setCommitmentLocationStatuses(
    commitmentStatuses: CommitmentStatusesView,
    location: LocationView,
): CommitmentStatusesView {
    let nextCmtStats = commitmentStatuses;
    // will not default commitmentStatuses to {} if undefined
    if (nextCmtStats) {
        nextCmtStats = { ...nextCmtStats };
        Object.keys(nextCmtStats).forEach((e) => {
            nextCmtStats[e] = {
                ...nextCmtStats[e],
                show: false,
            };
        });

        let key: string;
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        const identifier = location.id && location.id.toString().toLowerCase();
        // eslint-disable-next-line no-prototype-builtins
        if (nextCmtStats.hasOwnProperty(identifier)) {
            key = identifier;
        } else {
            // TODO revise - after sign off or after accept from summary dialog draft id is null. Can be router url need changes
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            const cmtId = location.id && location.id.toString().split('--')[0];
            key = `${cmtId}--undefined`;
        }

        if (key) {
            nextCmtStats[key] = {
                ...nextCmtStats[key],
                show: true,
            };
        }
    }

    return nextCmtStats;
}

export function setObservationLocationStatuses(
    observationStatuses: ObservationStatusesView,
    location: LocationView,
): ObservationStatusesView {
    let nextObsStats = observationStatuses;
    // will not default observationStatuses to {} if undefined
    if (nextObsStats) {
        nextObsStats = { ...nextObsStats };
        Object.keys(nextObsStats).forEach((e) => {
            nextObsStats[e] = false;
        });
        if (location.which === TabRefId.OBSERVATIONS && location.id != null) {
            const id = parseInt(location.id.toString().split('--')[0]);
            nextObsStats[id] = true;
        }
    }

    return nextObsStats;
}

export function setLocationActions(
    commitmentCategories: CommitmentCategory[],
    location: LocationView,
): CommitmentCategory[] {
    const nextCmtCats = commitmentCategories ? [...commitmentCategories] : [];
    nextCmtCats.forEach((cat) => {
        // reset
        cat.items = cat.items.map((e) => {
            const { hasEditAction, hasAddAction } = determineActions(
                e.statusId,
                e.disputedByMe,
                e.disputedByOtherParticipant,
                e.isOriginalCommitment,
            );
            return {
                ...e,
                hasEditAction,
                hasAddAction,
            };
        });
        // apply current location
        const index = cat.items.findIndex((e) => {
            return location.id != null && e.cmtIdentifier === location.id;
        });
        if (index === -1) {
            return;
        }
        cat.items[index] = {
            ...cat.items[index],
            hasEditAction: false,
            hasAddAction: false,
        };
    });

    return nextCmtCats;
}

export function ensureCategoryOpenState(
    commitmentCategories: CommitmentCategory[],
    observationCategories: ObservationCategory[],
    category: CommitmentCategory | ObservationCategory,
    isOpen: boolean,
): [CommitmentCategory[], ObservationCategory[]] {
    let nextNextCmtCats = commitmentCategories;
    if (nextNextCmtCats) {
        const index = nextNextCmtCats.indexOf(category as CommitmentCategory);
        if (index !== -1) {
            nextNextCmtCats = [...commitmentCategories];
            nextNextCmtCats[index].isOpen = isOpen;
        }
    }

    let nextNextObsCats = observationCategories;
    if (nextNextObsCats) {
        const index = nextNextObsCats.indexOf(category as ObservationCategory);
        if (index !== -1) {
            nextNextObsCats = [...observationCategories];
            nextNextObsCats[index].isOpen = isOpen;
        }
    }
    return [nextNextCmtCats, nextNextObsCats];
}

export function updateObservationCategoryOpenState(
    observationCategories: ObservationCategory[],
    cmtIdentifier: string,
): ObservationCategory[] {
    const nextNextObsCats = observationCategories;

    if (!cmtIdentifier && nextNextObsCats) {
        // eslint-disable-next-line no-return-assign
        nextNextObsCats.forEach((e) => (e.isOpen = false));
    }
    if (cmtIdentifier && nextNextObsCats) {
        const isPublished = !!cmtIdentifier.split('-')[2];
        cmtIdentifier = cmtIdentifier.replace(/(.*)-.*/, '$1');
        const cat = nextNextObsCats.find(
            (e) => e.obsIdentifier === cmtIdentifier,
        );
        const index = nextNextObsCats.indexOf(cat);

        if (index !== -1 && cat.published === isPublished) {
            nextNextObsCats[index].isOpen = true;
            nextNextObsCats[index].focusItems.publishedItems = {
                ...nextNextObsCats[index].focusItems.publishedItems,
                isOpen: true,
            };
        }
    }
    return nextNextObsCats;
}

function findCommitmentIdByIdentifier(
    commitmentCategories: CommitmentCategory[],
    identifier: string,
): number {
    for (const cat of commitmentCategories) {
        const item = cat.items.find(
            (ee) => composeTagIdentifierNext(ee) === identifier,
        );
        if (item) {
            return item.commitmentId;
        }
    }
    return null;
}

/** note that for commitments this will override what
 * commitmentSummaryViewToStatusViewTransformer() created until the next reload occurs */
export function setStatuses(
    commitmentStatuses: CommitmentStatusesView,
    observationStatuses: ObservationStatusesView,
    location: LocationView,
    commitmentCategories: CommitmentCategory[],
    statuses: StatusView[],
): [CommitmentStatusesView, ObservationStatusesView] {
    let nextCmtStats = commitmentStatuses;
    // will not default commitmentStatuses to {} if undefined
    if (nextCmtStats) {
        nextCmtStats = { ...nextCmtStats };
        statuses
            .reduce<StatusView[]>((p, c) => {
                let id = c.id;
                if (id == null) {
                    id = findCommitmentIdByIdentifier(
                        commitmentCategories,
                        c.identifier,
                    );
                }
                if (id != null) {
                    p.push({ ...c, id });
                }
                return p;
            }, [])
            .filter((e) => {
                // current location takes priority
                return (
                    e.which === TabRefId.COMMITMENTS &&
                    (location.which !== TabRefId.COMMITMENTS ||
                        e.id !== location.id)
                );
            })
            .forEach((e) => {
                nextCmtStats[e.id] = {
                    show: true,
                    type: e.type,
                    label: StatusViewTypeLabelMap[e.type],
                    color: StatusViewTypeColorMap[e.type],
                };
            });
    }

    let nextObsStats = observationStatuses;
    // will not default observationStatuses to {} if undefined
    if (nextObsStats) {
        nextObsStats = { ...nextObsStats };
        statuses
            .filter((e) => {
                // current location takes priority
                return (
                    e.which === TabRefId.OBSERVATIONS &&
                    (location.which !== TabRefId.OBSERVATIONS ||
                        e.id !== location.id)
                );
            })
            .forEach((e) => {
                nextObsStats[e.id] = e.active;
            });
    }

    return [nextCmtStats, nextObsStats];
}

export function getCategoryAndId(
    commitmentCategories: CommitmentCategory[],
    observationCategories: ObservationCategory[],
    which: TabRefId,
    id: number | string,
    identifier?: FocusIdentifier,
): {
    category: CommitmentCategory | ObservationCategory;
    id: number | string;
} {
    let category: CommitmentCategory | ObservationCategory;
    const itemId = id;
    if (which === TabRefId.COMMITMENTS) {
        category = commitmentCategories.find((cat) => {
            return cat.items.some((item) => item.cmtIdentifier === id);
        });
    }
    if (which === TabRefId.OBSERVATIONS && id != null) {
        id = parseInt(id.toString().split('--')[0]);
        category = observationCategories.find((cat) => {
            if (cat.behaviour) {
                return (
                    cat.focusItems.publishedItems.focusContainers.some(
                        (e) => e.observationId === id,
                    ) ||
                    cat.focusItems.draftItems.focusContainers.some(
                        (e) => e.observationId === id,
                    )
                );
            } else {
                return cat.focusItems.draftItems.focusContainers.some(
                    (e) => e.observationId === id,
                );
            }
        });
        return { category, id };
    }
    return { category, id: itemId };
}

export function reapplyIsOpenState(
    cmtCats: CommitmentCategory[],
    obsCats: ObservationCategory[],
    nextCmtCats: CommitmentCategory[],
    nextObsCats: ObservationCategory[],
): void {
    [
        [cmtCats, nextCmtCats],
        [obsCats, nextObsCats],
    ].forEach((pair) => {
        if (!pair[0] || !pair[1]) {
            return;
        }
        if (pair[0] && pair[0].length === pair[1].length) {
            const cat = pair[0] as Array<{ isOpen: boolean }>;
            cat.forEach((e, ei) => {
                pair[1][ei].isOpen = pair[0][ei].isOpen;
            });
        }
    });
}
